import { useState, useEffect } from "react";
import { B, PORTFOLIO_API } from "../../design/tokens";
import Reveal from "../shared/Reveal";
import Tag from "../shared/Tag";

const MOBILE_LIMIT = 3;

export default function PortfolioSection() {
  const [filter, setFilter]       = useState("All");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 768);
  const [showAll, setShowAll]     = useState(false);
  const filters = ["All", "Residential", "Commercial", "Agricultural", "Industrial"];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!process.env.REACT_APP_PORTFOLIO_API_URL) { setLoading(false); return; }
    fetch(PORTFOLIO_API)
      .then(r => r.json())
      .then(d => setProperties(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? properties : properties.filter(p => p.type === filter);
  const shown = isMobile && !showAll ? filtered.slice(0, MOBILE_LIMIT) : filtered;
  const hasMore = isMobile && !showAll && filtered.length > MOBILE_LIMIT;
  const statusColors = { "Active Listing": B.success, "Under Appraisal": B.sky, "Title Processing": B.gold, "Property Managed": B.mist, "Appraisal Complete": B.azure };

  return (
    <section id="portfolio" style={{ background: B.ghost, padding: "120px 0" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>
        <Reveal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginBottom: 52 }}>
            <div>
              <Tag text="Portfolio" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 700, color: B.navy, lineHeight: 1.1, letterSpacing: -0.5 }}>
                Properties &<br /><em style={{ color: B.azure }}>Active Engagements</em>
              </h2>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {filters.map(f => (
                <button key={f} onClick={() => { setFilter(f); setShowAll(false); }} style={{ padding: "9px 20px", cursor: "pointer", transition: "all 0.25s", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 500, background: filter === f ? B.navy : B.white, color: filter === f ? B.white : B.slate, border: `1.5px solid ${filter === f ? B.navy : B.border}`, boxShadow: filter === f ? `0 2px 14px ${B.navy}22` : "none" }}
                  onMouseEnter={e => { if (filter !== f) { e.currentTarget.style.borderColor = B.azure; e.currentTarget.style.color = B.azure; } }}
                  onMouseLeave={e => { if (filter !== f) { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.color = B.slate; } }}
                >{f}</button>
              ))}
            </div>
          </div>
        </Reveal>
        {loading && (
          <div style={{ textAlign: "center", padding: "64px 0", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, color: B.slateLight }}>Loading portfolio…</div>
        )}
        {!loading && shown.length === 0 && (
          <div style={{ textAlign: "center", padding: "64px 0", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, color: B.slateLight }}>No portfolio items available.</div>
        )}
        {!loading && shown.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 20 }} className="portfolio-grid">
          {shown.map((p, i) => (
            <Reveal key={p.portfolioId || p.id} delay={0.07 * i} style={!isMobile && shown.length >= 3 && i === 0 ? { gridColumn: "1 / 3" } : {}}>
              <div style={{ position: "relative", overflow: "hidden", height: !isMobile && shown.length >= 3 && i === 0 ? 420 : 300, cursor: "pointer", transition: "transform 0.4s ease, box-shadow 0.4s ease", boxShadow: `0 4px 24px ${B.navy}0d` }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = `0 16px 48px ${B.navy}28`; e.currentTarget.querySelector(".po").style.opacity = "1"; e.currentTarget.querySelector(".img").style.transform = "scale(1.05)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 4px 24px ${B.navy}0d`; e.currentTarget.querySelector(".po").style.opacity = "0"; e.currentTarget.querySelector(".img").style.transform = "scale(1)"; }}
              >
                {p.imageUrl
                  ? <img className="img" src={p.imageUrl} alt={p.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", transition: "transform 0.6s ease" }} />
                  : <div className="img" style={{ position: "absolute", inset: 0, background: B.navy, transition: "transform 0.6s ease" }} />
                }
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(0deg, ${B.ink}cc 0%, ${B.navy}55 40%, transparent 70%)` }} />
                <div className="po" style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${B.navy}dd 0%, ${B.royal}99 100%)`, opacity: 0, transition: "opacity 0.35s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {p.url && (
                    <button onClick={() => window.open(p.url, "_blank", "noopener,noreferrer")} style={{ background: B.azure, color: B.white, border: "none", padding: "12px 28px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, fontWeight: 600, cursor: "pointer", letterSpacing: 0.5 }}>View Details →</button>
                  )}
                </div>
                <div style={{ position: "absolute", top: 16, left: 16, background: `${B.azure}cc`, backdropFilter: "blur(8px)", padding: "4px 14px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600, color: B.white, letterSpacing: 1 }}>{p.type}</div>
                <div style={{ position: "absolute", top: 16, right: 16, background: `${statusColors[p.status]}22`, backdropFilter: "blur(8px)", border: `1px solid ${statusColors[p.status]}55`, padding: "4px 14px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600, color: statusColors[p.status] || B.mist, letterSpacing: 1 }}>{p.status}</div>
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 22px" }}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: B.white, marginBottom: 6, lineHeight: 1.25 }}>{p.title}</h3>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: `${B.white}cc` }}>📍 {p.location}</span>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: `${B.white}cc` }}>📐 {p.area}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        )}
        {hasMore && (
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <button
              onClick={() => setShowAll(true)}
              style={{ padding: "12px 36px", cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, background: B.navy, color: B.white, border: "none", letterSpacing: 0.5, boxShadow: `0 2px 14px ${B.navy}22` }}
            >
              Show All ({filtered.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
